import { useCallback, useEffect, useState } from 'react';
import { useApi, useAuth } from '../auth/AuthProvider';
import { useWebPushKey } from '../api/queries';
import { useTranslation } from '../i18n/I18nProvider';
import { Button, Card, Switch, useToast } from '../ui';
import { IconBell } from './icons';

/**
 * Obavijesti u pregledniku.
 *
 * Trener radi u portalu, a mobilna aplikacija je klijentska — do sada je svaka
 * obavijest prema njemu (poruka, novi unos prehrane) mogla završiti samo u
 * zvoncu koje mora biti otvoreno da bi se vidjelo. Ovo je jedini put kojim ga
 * obavijest stigne dok portal nije u prvom planu.
 *
 * Stanja se ne pogađaju nego čitaju: dopuštenje preglednika, postoji li već
 * pretplata i je li poslužitelj uopće postavljen. Blokirano dopuštenje se
 * prekidačem NE može vratiti, pa se tada nudi uputa umjesto gumba koji ne radi.
 */
type Stanje =
  | 'ucitavanje'
  | 'nepodrzano'
  | 'nespremno'
  | 'nepostavljeno'
  | 'blokirano'
  | 'ukljuceno'
  | 'iskljuceno';

function podrzano(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/** VAPID ključ ide u `subscribe` kao bajtovi, ne kao base64url niz. */
function kljucUBajtove(base64url: string): ArrayBuffer {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const bin = atob(b64 + pad);
  const spremnik = new ArrayBuffer(bin.length);
  const out = new Uint8Array(spremnik);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return spremnik;
}

function bajtoviUKljuc(buf: ArrayBuffer): string {
  let bin = '';
  const b = new Uint8Array(buf);
  for (let i = 0; i < b.length; i += 1) bin += String.fromCharCode(b[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function BrowserNotifications() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const api = useApi();
  const { user } = useAuth();
  const kljuc = useWebPushKey();
  const [stanje, setStanje] = useState<Stanje>('ucitavanje');
  const [busy, setBusy] = useState(false);

  const publicKey = kljuc.data?.publicKey ?? null;

  /** Pošalji pretplatu poslužitelju. Vraća je li preuzeta s drugog računa. */
  const prijavi = useCallback(
    async (sub: PushSubscription) => {
      const kljucevi = sub.toJSON().keys ?? {};
      const odgovor = await api.notifications.registerPushToken({
        token: sub.endpoint,
        platform: 'web',
        p256dh: kljucevi.p256dh ?? '',
        auth: kljucevi.auth ?? '',
      });
      return odgovor.moved;
    },
    [api],
  );

  const osvjezi = useCallback(async () => {
    if (!podrzano()) return setStanje('nepodrzano');
    if (kljuc.isPending) return;
    if (!publicKey) return setStanje('nepostavljeno');
    if (Notification.permission === 'denied') return setStanje('blokirano');
    /*
      U lokalnom razvoju service worker se namjerno ne registrira (presretao bi
      Viteov HMR), pa ovdje registracije nema. To je stanje, ne greška.
    */
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return setStanje('nespremno');
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return setStanje('iskljuceno');

    /*
      Pretplata pripada PREGLEDNIKU, ne računu: jedan Chrome ima jednu. Kad se
      na istom pregledniku prijavi drugi korisnik i uključi obavijesti, server
      je prebaci na njega — inače bi mu stizale tuđe obavijesti. Prvom računu
      je prekidač i dalje pisao „uključeno", a obavijest više nije dolazila.
      Zato se pri svakom otvaranju Profila pretplata potvrdi za prijavljenog
      korisnika: „uključeno" tada znači uključeno za onoga tko ovdje gleda.
    */
    try {
      await prijavi(sub);
    } catch {
      /* Mreža zna pasti; stanje prekidača zbog toga ne mijenjamo. */
    }
    setStanje('ukljuceno');
  }, [kljuc.isPending, prijavi, publicKey]);

  useEffect(() => {
    void osvjezi();
  }, [osvjezi]);

  async function ukljuci() {
    if (!publicKey) return;
    setBusy(true);
    try {
      const dopustenje = await Notification.requestPermission();
      if (dopustenje !== 'granted') {
        setStanje(dopustenje === 'denied' ? 'blokirano' : 'iskljuceno');
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return setStanje('nespremno');

      /*
        Pretplata je vezana uz ključ kojim je stvorena. Ako se ključ na
        poslužitelju ikad promijeni, stara pretplata se ne može ni obnoviti ni
        koristiti — `subscribe` puca. Zato se prvo makne, pa napravi nova.
      */
      const stara = await reg.pushManager.getSubscription();
      if (stara) {
        const stariKljuc = stara.options.applicationServerKey;
        if (!stariKljuc || bajtoviUKljuc(stariKljuc) !== publicKey) await stara.unsubscribe();
      }

      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: kljucUBajtove(publicKey),
        }));

      const preuzeto = await prijavi(sub);
      setStanje('ukljuceno');
      // Bez ove poruke prethodni racun tiho ostane bez obavijesti na ovom pregledniku.
      toast(preuzeto ? t('webPush.moved') : t('webPush.enabled'), 'success');
    } catch {
      toast(t('webPush.failed'), 'danger');
      await osvjezi();
    } finally {
      setBusy(false);
    }
  }

  async function iskljuci() {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        // Prvo poslužitelj: ako otkazivanje u pregledniku prođe a zapis ostane,
        // red bi zauvijek slao na endpoint koji više ne postoji.
        await api.notifications.removePushToken(sub.endpoint);
        await sub.unsubscribe();
      }
      setStanje('iskljuceno');
      toast(t('webPush.disabled'), 'success');
    } catch {
      toast(t('webPush.failed'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function proba() {
    setBusy(true);
    try {
      await api.notifications.test();
    } finally {
      setBusy(false);
    }
  }

  if (stanje === 'ucitavanje') return null;

  const poruka: Partial<Record<Stanje, string>> = {
    nepodrzano: t('webPush.unsupported'),
    nespremno: t('webPush.notReady'),
    nepostavljeno: t('webPush.notConfigured'),
  };

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-accent-light text-accent-deep"
        >
          <IconBell width={22} height={22} />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-[20px] font-semibold text-ink">{t('webPush.title')}</h2>
          <p className="mt-1 text-[14px] leading-relaxed text-ink-muted">{t('webPush.body')}</p>
        </div>
      </div>

      {poruka[stanje] ? (
        <p className="rounded-lg bg-bg-alt p-3 text-[13.5px] leading-relaxed text-ink-muted">
          {poruka[stanje]}
        </p>
      ) : stanje === 'blokirano' ? (
        <div className="rounded-lg bg-bg-alt p-3">
          <p className="text-[13.5px] font-medium text-ink">{t('webPush.blocked')}</p>
          <p className="mt-1 text-[13.5px] leading-relaxed text-ink-muted">
            {t('webPush.blockedHelp')}
          </p>
        </div>
      ) : (
        <>
          {/*
            Natpis imenuje STVAR, ne radnju. Prva verzija je pisala „Isključi
            obavijesti" dok su bile uključene, pa se nije dalo razaznati govori
            li natpis o stanju ili o tome što će se dogoditi na klik.
          */}
          <Switch
            id="web-push"
            label={t('webPush.switch')}
            description={stanje === 'ukljuceno' ? t('webPush.on') : t('webPush.off')}
            checked={stanje === 'ukljuceno'}
            disabled={busy}
            onCheckedChange={(v) => void (v ? ukljuci() : iskljuci())}
          />
          {stanje === 'ukljuceno' && (user?.role === 'trainer' || user?.role === 'admin') && (
            <Button
              variant="secondary"
              className="self-start"
              loading={busy}
              onClick={() => void proba()}
            >
              {t('webPush.test')}
            </Button>
          )}
        </>
      )}
    </Card>
  );
}
